import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Send, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import { sanitizeHtml } from '../lib/sanitize';
import { useSite } from '../context/SiteContext';
import SEO from '../components/SEO';

const emptyForm = { name: '', email: '', subject: '', message: '' };

function newChallenge() {
    return { a: Math.floor(Math.random() * 9) + 1, b: Math.floor(Math.random() * 9) + 1 };
}

export default function ContactPage() {
    const { settings } = useSite();
    const [form, setForm] = useState(emptyForm);
    const [captcha, setCaptcha] = useState(newChallenge);
    const [answer, setAnswer] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [intro, setIntro] = useState('');

    useEffect(() => {
        api.get('/pages/contact').then((r) => setIntro(r.data?.body || '')).catch(() => {});
    }, []);

    const resetCaptcha = useCallback(() => {
        setCaptcha(newChallenge());
        setAnswer('');
    }, []);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error('Please fill in your name, email and message.');
            return;
        }
        if (parseInt(answer, 10) !== captcha.a + captcha.b) {
            toast.error('Incorrect security answer. Please try again.');
            resetCaptcha();
            return;
        }
        setSubmitting(true);
        try {
            await api.post('/contact', {
                name: form.name.trim(),
                email: form.email.trim(),
                subject: form.subject.trim(),
                message: form.message.trim(),
            });
            toast.success('Thank you! Your message has been sent.');
            setForm(emptyForm);
            resetCaptcha();
        } catch (err) {
            const msg = err?.response?.data?.detail;
            toast.error(typeof msg === 'string' ? msg : 'Could not send your message. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <SEO
                title="Contact"
                description={`Get in touch with ${settings?.brand_name || 'by anfal'}.`}
                canonical="/contact"
                type="article"
            />
            <div className="max-w-[760px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16" data-testid="contact-page">
                <div className="text-center mb-10">
                    <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>by anfal</p>
                    <h1 className="font-serif text-5xl md:text-6xl mt-2 shine-text" data-testid="contact-page-title">Contact</h1>
                    <hr className="divider-burgundy mt-5 mx-auto" />
                    {intro && (
                        <div
                            className="prose-luxury mt-6 text-center mx-auto"
                            style={{ color: 'var(--ink-muted)' }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(intro) }}
                        />
                    )}
                </div>

                <form onSubmit={onSubmit} className="luxury-card p-6 lg:p-8 space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <label className="eyebrow block mb-1.5" style={{ color: 'var(--brand-burgundy)' }}>Name</label>
                            <input
                                type="text"
                                required
                                value={form.name}
                                onChange={(e) => update('name', e.target.value)}
                                placeholder="Your name"
                                data-testid="contact-name-input"
                                className="input-luxury"
                            />
                        </div>
                        <div>
                            <label className="eyebrow block mb-1.5" style={{ color: 'var(--brand-burgundy)' }}>Email</label>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => update('email', e.target.value)}
                                placeholder="you@email.com"
                                data-testid="contact-email-input"
                                className="input-luxury"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="eyebrow block mb-1.5" style={{ color: 'var(--brand-burgundy)' }}>Subject</label>
                        <input
                            type="text"
                            value={form.subject}
                            onChange={(e) => update('subject', e.target.value)}
                            placeholder="How can we help?"
                            data-testid="contact-subject-input"
                            className="input-luxury"
                        />
                    </div>
                    <div>
                        <label className="eyebrow block mb-1.5" style={{ color: 'var(--brand-burgundy)' }}>Message</label>
                        <textarea
                            required
                            rows={6}
                            value={form.message}
                            onChange={(e) => update('message', e.target.value)}
                            placeholder="Write your message..."
                            data-testid="contact-message-input"
                            className="input-luxury resize-none"
                        />
                    </div>

                    {/* Math captcha */}
                    <div className="flex flex-col sm:flex-row sm:items-end gap-3 rounded-xl p-4" style={{ backgroundColor: 'var(--brand-rose)' }}>
                        <div className="flex-1">
                            <label className="eyebrow block mb-1.5" style={{ color: 'var(--brand-burgundy)' }}>Security check</label>
                            <div className="flex items-center gap-3">
                                <span className="font-serif text-2xl select-none" style={{ color: 'var(--brand-burgundy)' }} data-testid="contact-captcha-question">
                                    {captcha.a} + {captcha.b} = ?
                                </span>
                                <button
                                    type="button"
                                    onClick={resetCaptcha}
                                    aria-label="New question"
                                    data-testid="contact-captcha-refresh"
                                    className="p-2 rounded-lg hover:bg-white/60 transition-colors"
                                >
                                    <RefreshCw className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} />
                                </button>
                            </div>
                        </div>
                        <input
                            type="number"
                            required
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            placeholder="Answer"
                            data-testid="contact-captcha-input"
                            className="input-luxury sm:max-w-[140px]"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit-button"
                        className="btn-burgundy w-full sm:w-auto px-7 py-3 rounded-xl text-xs tracking-[0.20em] uppercase font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                        {submitting ? 'Sending...' : (<>Send Message <Send className="w-4 h-4" /></>)}
                    </button>
                </form>
            </div>
        </>
    );
}
